import type { IDestinationItem, IPromoItem } from '@/components/clp/types'

/**
 * Composable for managing price tabs functionality in CLP Destinations component
 * This is specific to the destinations groupd prices feature and not intended for general use
 */
export function usePriceTabs(
  groupDestinationsAndPromoItems: (destinations: IDestinationItem[]) => (IDestinationItem | IPromoItem)[]
) {
  const destinations = ref<IDestinationItem[]>([])
  const activeTab = ref<number>(0)
  const groupedPrices = ref<Map<number, IDestinationItem[]>>(new Map())

  function setDestinations(newDestinations: IDestinationItem[]) {
    destinations.value = newDestinations
    setGroupedPrices()
    activeTab.value = priceTabKeys.value.length ? priceTabKeys.value[0] : 0
  }

  function setGroupedPrices() {
    const priceKey = destinations.value.every((item) => !!item.priceOW) ? 'priceOW' : 'priceRT'

    groupedPrices.value = destinations.value.reduce((acc, curr) => {
      if (curr[priceKey] && acc.has(curr[priceKey])) {
        acc.get(curr[priceKey])?.push(curr)
      } else {
        acc.set(curr[priceKey], [curr])
      }

      return acc
    }, new Map<number, IDestinationItem[]>())
  }

  const activeGroupPrices = computed(() =>
    groupDestinationsAndPromoItems(groupedPrices.value.get(activeTab.value) ?? [])
  )

  const areAvailable = computed(() => groupedPrices.value.size)

  // Sort price tabs keys ascending
  const priceTabKeys = computed(() => Array.from(groupedPrices.value.keys()).sort((a, b) => a - b))

  return {
    activeTab,
    groupedPrices,
    activeGroupPrices,
    areAvailable,
    priceTabKeys,
    setDestinations
  }
}
